<script setup lang="ts">
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import { getActivityIconFromSlug } from '@/composables/activityFeedIcons'
import ActivityCardBase from './ActivityCardBase.vue'
import { isImportedPointType, isWebappPointType } from './pointActivityTypes'
import { getLocalizedActivityText } from './localizedActivityText'
import ActivityMessageCard from './ActivityMessageCard.vue'
import ActivityMailCard from './ActivityMailCard.vue'
import ActivityCheckinCard from './ActivityCheckinCard.vue'
import ActivityReferenceStoreChangedCard from './ActivityReferenceStoreChangedCard.vue'
import ActivityRewardCard from './ActivityRewardCard.vue'
import ActivitySurveyCard from './ActivitySurveyCard.vue'
import ActivityContestCard from './ActivityContestCard.vue'
import ActivitySegmentCard from './ActivitySegmentCard.vue'
import ActivityPointCard from './ActivityPointCard.vue'
import ActivityAutopilotCard from './ActivityAutopilotCard.vue'
import ActivityAnswerCrmCard from './ActivityAnswerCrmCard.vue'
import ActivityMissedCallCard from './ActivityMissedCallCard.vue'
import ActivityMarketingFlowCard from './ActivityMarketingFlowCard.vue'
import ActivityConsentCard from './ActivityConsentCard.vue'

const { t, locale } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const fallbackIcon = computed(() => getActivityIconFromSlug(props.activity.icon) || 'HeroInformationCircleIconSolid')
const fallbackDescription = computed(() => getLocalizedActivityText(props.activity.description, locale.value))
const fallbackTitle = computed(() => {
  const title = getLocalizedActivityText(props.activity.title, locale.value)
  return title || (fallbackDescription.value ? '' : t('pages.customer.customerDetailPage.blocks.activity.unknown.text'))
})

const isMessageType = (type: string): boolean => {
  return (
    type === 'campaign.message.sent' ||
    type === 'autopilot.message.sent' ||
    type === 'marketing.flow.message.sent' ||
    type === 'marketing.flow.push.sent' ||
    type === 'order.notification.sent' ||
    type === 'reward.message.sent' ||
    type === 'contest.game.notification.sent' ||
    type === 'missed.call.notification.sent' ||
    type === 'customer.notification.sent'
  )
}

const isCheckinType = (type: string): boolean => {
  return type === 'checkin'
}

const isReferenceStoreChangedType = (type: string): boolean => {
  return type === 'reference_store.changed'
}

const isRewardType = (type: string): boolean => {
  if (type === 'reward.claimed' || type === 'reward.used') return true
  // To remove when we remove the customer_point with type "reward" from the backend (replace with dedicated reward.claimed)
  if (type === 'customer_point' && props.activity.data?.type === 'reward') return true
  return false
}

const isSurveyType = (type: string): boolean => {
  return type === 'survey.answer'
}

const isContestType = (type: string): boolean => {
  return type === 'contest.participation'
}

const isSegmentType = (type: string): boolean => {
  return type === 'segment.join' || type === 'segment.leave'
}

const isPointType = (type: string): boolean => {
  // To remove when we remove the customer_point with type "reward" from the backend (replace with dedicated points.manual and points.imported)
  if (type === 'customer_point' && props.activity.data?.type === 'reward') return false
  return type === 'points.manual' || isImportedPointType(type) || type === 'customer_point' || isWebappPointType(type)
}

const isAutopilotType = (type: string): boolean => {
  return type === 'autopilot.message.converted'
}

const isAnswerCrmType = (type: string): boolean => {
  return type === 'enrichment.answer'
}

const isMissedCallType = (type: string): boolean => {
  return type === 'missed_call'
}

const isMarketingFlowType = (type: string): boolean => {
  return type === 'marketing.flow' || type === 'marketing.flow.execution'
}

const isConsentType = (type: string): boolean => {
  return ['email.optin', 'email.optout', 'email.optunknown', 'sms.optin', 'sms.optout', 'sms.optunknown'].includes(type)
}
</script>

<template>
  <template v-if="isMessageType(activity.type)">
    <ActivityMailCard v-if="activity.data?.channel === 'mail'" :activity="activity" :show-connector="showConnector" />
    <ActivityMessageCard v-else :activity="activity" :show-connector="showConnector" />
  </template>
  <ActivityCheckinCard v-else-if="isCheckinType(activity.type)" :activity="activity" :show-connector="showConnector" />
  <ActivityReferenceStoreChangedCard
    v-else-if="isReferenceStoreChangedType(activity.type)"
    :activity="activity"
    :show-connector="showConnector"
  />
  <ActivityRewardCard v-else-if="isRewardType(activity.type)" :activity="activity" :show-connector="showConnector" />
  <ActivitySurveyCard v-else-if="isSurveyType(activity.type)" :activity="activity" :show-connector="showConnector" />
  <ActivityContestCard v-else-if="isContestType(activity.type)" :activity="activity" :show-connector="showConnector" />
  <ActivitySegmentCard v-else-if="isSegmentType(activity.type)" :activity="activity" :show-connector="showConnector" />
  <ActivityPointCard v-else-if="isPointType(activity.type)" :activity="activity" :show-connector="showConnector" />
  <ActivityAutopilotCard
    v-else-if="isAutopilotType(activity.type)"
    :activity="activity"
    :show-connector="showConnector"
  />
  <ActivityAnswerCrmCard
    v-else-if="isAnswerCrmType(activity.type)"
    :activity="activity"
    :show-connector="showConnector"
  />
  <ActivityMissedCallCard
    v-else-if="isMissedCallType(activity.type)"
    :activity="activity"
    :show-connector="showConnector"
  />
  <ActivityMarketingFlowCard
    v-else-if="isMarketingFlowType(activity.type)"
    :activity="activity"
    :show-connector="showConnector"
  />
  <ActivityConsentCard v-else-if="isConsentType(activity.type)" :activity="activity" :show-connector="showConnector" />
  <ActivityCardBase v-else :icon="fallbackIcon" :occurred-at="activity.occurred_at" :show-connector="showConnector">
    <template v-if="fallbackTitle" #title>
      <p class="text-sm font-medium text-foreground">{{ fallbackTitle }}</p>
    </template>
    <template v-if="fallbackDescription" #description>
      <p class="mt-0 text-sm text-muted-foreground">{{ fallbackDescription }}</p>
    </template>
  </ActivityCardBase>
</template>
